import {
	Accordion,
	BackIcon,
	EffectsIcon,
	IconButton,
	InfoIcon,
} from "@au-schein-de/au-ui-library";
import { CATEGORIES, isDeviceKind } from "@dransay/api/models";
import {
	type MouseEvent,
	Suspense,
	useCallback,
	useRef,
	useState,
} from "react";
import { useTranslation } from "react-i18next";
import {
	useLoaderData,
	useLocation,
	useNavigate,
	useRouteLoaderData,
} from "react-router";
import { useVendorSelection } from "~/contexts/vendor-selection.context";
import type { loader } from "~/routes/_storefront._products.product.$slug.$productId";
import type { Route } from "../../../.react-router/types/app/+types/root";
import { useCart } from "../cart/cart.context";
import {
	shouldShowExclusiveProductsPdpSection,
	shouldUseDesktopStickyPdpCta,
} from "./exclusive-products/exclusive-products-pdp.logic";
import { ExclusiveProductsPdpSection } from "./exclusive-products/exclusive-products-pdp-section.component";
import { usePdpExclusiveProducts } from "./exclusive-products/use-pdp-exclusive-products.hook";
import { ExpertReviewComponent } from "./expert-review/expert-review.component";
import { getShareUrl } from "./get-share-url";
import { ProductDetailsCTA } from "./product-details-cta.component";
import { ProductDetailsDescriptionAccordion } from "./product-details-description-accordion.component";
import { ProductDetailsHeader } from "./product-details-header.component";
import { ProductDetailsItem } from "./product-details-item.component";
import { ProductDetailsItemContent } from "./product-details-item-content.component";
import { ProductDetailsProducer } from "./product-details-producer.component";
import { ProductDetailsTerpenes } from "./product-details-terpenes.component";
import { ShareButton } from "./share-button.component";
import { useProductDetailsVendorState } from "./use-product-details-vendor-state.hook";

const getTasteTranslationKey = (
	thc: number | null,
): "taste.caramel" | "taste.banana" | "taste.strawberry" | "taste.unknown" => {
	if (thc === null) return "taste.unknown";

	switch (thc) {
		case 2.5:
			return "taste.caramel";
		case 5:
			return "taste.banana";
		case 7.5:
			return "taste.strawberry";
		default:
			return "taste.unknown";
	}
};

const shouldShowEdibleTaste = (
	category: string | null | undefined,
): boolean => {
	return category === CATEGORIES.EDIBLES || category === CATEGORIES.DRINKABLES;
};

export function ProductDetails() {
	const rootLoaderData =
		useRouteLoaderData<Route.ComponentProps["loaderData"]>("root");
	const enableVariantSelector =
		rootLoaderData?.featureFlags?.enableVariantSelector ?? false;
	const {
		product,
		carouselProducts,
		initialExclusiveVendorId,
		initialExclusiveDeliveryMethodKind,
		ratingBreakdown,
	} = useLoaderData<typeof loader>();
	const { selectedDeliveryMethodKind } = useVendorSelection();
	const {
		localVendorId,
		handleVendorChange,
		variants,
		effectivePreferredVendorId,
	} = useProductDetailsVendorState({
		product,
		enableVariantSelector,
	});
	const {
		name,
		thc,
		kind,
		details,
		producer,
		terpenes = [],
		showWarning,
		edibleTaste,
	} = product;

	const navigate = useNavigate();
	const location = useLocation();
	const { items: cartItems } = useCart();
	const { products: exclusiveProducts } = usePdpExclusiveProducts({
		productId: product.id,
		initialProducts: carouselProducts,
		initialVendorId: initialExclusiveVendorId,
		initialDeliveryMethodKind: initialExclusiveDeliveryMethodKind,
		vendorId: localVendorId,
		deliveryMethodKind: selectedDeliveryMethodKind,
	});
	const showExclusiveProductsSection = shouldShowExclusiveProductsPdpSection(
		cartItems.length,
		exclusiveProducts.length,
	);
	const isDesktopSticky = shouldUseDesktopStickyPdpCta(
		showExclusiveProductsSection,
	);
	const expertReviewRef = useRef<HTMLDivElement | null>(null);

	const enabledSections = {
		complaints:
			details.complaints.best.length + details.complaints.rest.length > 0,
		effects: details.effects.best.length + details.effects.rest.length > 0,
		flavors: details.flavors.best.length + details.flavors.rest.length > 0,
		taste: thc !== null,
		terpenes: terpenes.length > 0,
	};
	const isDeviceInhalationProduct =
		product.category === CATEGORIES.INHALATIONS_EXTRACT && isDeviceKind(kind);
	const showTerpeneBasedSections = !isDeviceInhalationProduct;

	const [expandedSections, setExpandedSections] = useState([
		"description",
		...(showTerpeneBasedSections ? ["complaints", "effects"] : []),
		"taste",
	]);

	const { t, i18n } = useTranslation(["common", "product"]);

	const getStrengthLabel = (
		strength: number,
		max: number,
		type: "complaints" | "effects" | "flavors",
	): string => {
		const ratio = max > 0 ? strength / max : 0;
		if (ratio >= 0.8) return t(`product:strength.${type}.very_high`);
		if (ratio >= 0.6) return t(`product:strength.${type}.high`);
		if (ratio >= 0.4) return t(`product:strength.${type}.medium`);
		if (ratio >= 0.2) return t(`product:strength.${type}.low`);
		return t(`product:strength.${type}.very_low`);
	};

	const parseExpertRating = (
		rating: string | null | undefined,
	): number | null => {
		if (!rating) return null;
		const parsed = Number(rating);
		return Number.isFinite(parsed) && parsed > 0 ? parsed : null;
	};

	const expertReviewRating = parseExpertRating(product.expertReviewRating);
	const expertReview =
		product.showExpertReview && expertReviewRating !== null
			? {
					overallRating: expertReviewRating,
					reviewerName: product.doctorName || "",
					reviewAppearance: product.reviewAppearance,
					reviewAroma: product.reviewAroma,
					reviewFlavor: product.reviewFlavor,
					reviewEffects: product.reviewEffects,
					reviewConclusion: product.reviewConclusion,
				}
			: undefined;

	const showExpertReview = expertReview !== undefined;

	const handleExpertReviewClick = useCallback(() => {
		const target = expertReviewRef.current;
		if (!target) return;

		if (typeof window === "undefined") {
			return target.scrollIntoView({ behavior: "smooth", block: "start" });
		}

		const offset = window.innerWidth >= 768 ? 140 : 120;
		const top = target.getBoundingClientRect().top + window.scrollY - offset;
		window.scrollTo({ top: Math.max(top, 0), behavior: "smooth" });
	}, []);

	const handleGoBack = useCallback(
		async (e: MouseEvent<unknown>) => {
			e.preventDefault();

			if (location.state?.from === "/products") {
				navigate(-1);
			} else {
				navigate(`/products${location.search}`);
			}
		},
		[navigate, location],
	);

	const productFullName = `${name}: ${kind}`;
	const language = i18n.language;
	const warningText: string =
		product[language === "de" ? "warningDe" : "warningEn"];

	// Consolidated flag to determine if warning banner should render
	const showWarningBanner = showWarning && Boolean(warningText?.trim());

	return (
		<div className="-mx-4 md:mx-0">
			<div className="mt-0 md:mt-4 notranslate" translate="no">
				<div className="w-full">
					<div className="flex md:hidden items-center justify-between bg-gray-100 pt-2 px-4">
						<IconButton
							type="button"
							aria-label={t("common:backToAllProducts")}
							icon={<BackIcon size={6} colorToken="gray-700" />}
							shape="circle"
							color="gray"
							className="size-11 min-h-11 min-w-11 bg-white border-0 shadow-none hover:bg-white active:bg-white"
							onClick={handleGoBack}
							data-testid="product-details-back-mobile"
						/>
						<ShareButton
							title={t("product:share.product.title", {
								productName: productFullName,
							})}
							text={t("product:share.product.text", {
								productName: productFullName,
							})}
							url={
								typeof window !== "undefined"
									? getShareUrl(window.location.href)
									: ""
							}
						/>
					</div>
				</div>

				<div className="grid grid-cols-1 md:grid-cols-2 gap-2 w-full mt-2 md:mt-0 mb-0 [grid-template-areas:'header'_'cta'_'description'_'accordion'_'exclusive'] md:[grid-template-areas:'header_cta'_'description_cta'_'accordion_cta']">
					{/* Header (with optional warning inside) - area: header */}
					<div className="[grid-area:header]">
						<div className="grid grid-cols-1 grid-rows-[226px_auto] md:grid-rows-[340px_auto]">
							<ProductDetailsHeader
								product={product}
								badges={["exclusive", "recommended"]}
								backLabel={t("common:backToAllProducts")}
								onBack={handleGoBack}
							/>
						</div>
						{showWarningBanner && (
							<div className="mt-4 rounded-md bg-error-100 px-4 py-3 text-body-sm flex items-start gap-2">
								<span className="text-gray-700 mt-[2px]">
									<InfoIcon.Filled colorToken={"error-500"} size={4} />
								</span>
								<p className="text-gray-700 text-body-sm">
									<span className="text-body-sm-emphasis">
										{t("product:product.warning.prefix")}
									</span>{" "}
									{warningText}
								</p>
							</div>
						)}
					</div>

					{/* CTA + exclusive products — area: cta (right column on desktop) */}
					<div className="[grid-area:cta] flex flex-col gap-2">
						<ProductDetailsCTA
							product={product}
							showExpertReview={showExpertReview}
							expertReview={expertReview}
							onExpertReviewClick={handleExpertReviewClick}
							ratingBreakdown={ratingBreakdown}
							localVendorId={localVendorId}
							onLocalVendorChange={handleVendorChange}
							variants={variants}
							effectivePreferredVendorId={effectivePreferredVendorId}
							isDesktopSticky={isDesktopSticky}
						/>
						{showExclusiveProductsSection && (
							<div className="hidden md:block">
								<ExclusiveProductsPdpSection products={exclusiveProducts} />
							</div>
						)}
					</div>

					{/* Description + optional Expert Review — single grid area */}
					<Suspense
						fallback={
							<div className="[grid-area:description] space-y-2">
								<div className="h-32 animate-pulse bg-gray-100 rounded-lg" />
								{expertReview && (
									<div className="h-48 animate-pulse bg-gray-100 rounded-lg" />
								)}
							</div>
						}
					>
						<div className="flex flex-col gap-2 [grid-area:description]">
							<Accordion.Root
								className="w-full flex flex-col"
								type="multiple"
								value={expandedSections}
								onValueChange={setExpandedSections}
							>
								<Accordion.Item value="description" className="px-6 pt-6">
									<ProductDetailsDescriptionAccordion product={product} />
								</Accordion.Item>
							</Accordion.Root>

							{expertReview && (
								<div ref={expertReviewRef}>
									<ExpertReviewComponent review={expertReview} />
								</div>
							)}
						</div>
					</Suspense>

					{/* Accordion - area: accordion (Suspense for selective hydration) */}
					<Suspense
						fallback={
							<div className="[grid-area:accordion] space-y-2">
								<div className="h-14 animate-pulse bg-gray-100 rounded-lg" />
								<div className="h-14 animate-pulse bg-gray-100 rounded-lg" />
								<div className="h-14 animate-pulse bg-gray-100 rounded-lg" />
							</div>
						}
					>
						<Accordion.Root
							className="w-full flex flex-col gap-2 [grid-area:accordion]"
							type="multiple"
							value={expandedSections}
							onValueChange={setExpandedSections}
						>
							{showTerpeneBasedSections && (
								<>
									<Accordion.Item value="complaints">
										<ProductDetailsItem
											title={t("common:complaints")}
											info={t("common:complaintsInfo")}
											secondaryCharacteristic={details.complaints.rest.map(
												(c: string) =>
													// @ts-ignore: dynamic key generation
													t(`product:filters.complaints.${c}`),
											)}
											hasData={enabledSections.complaints}
										>
											<ul className="mt-4 mb-2">
												{(
													Object.values(details.complaints.best) as Array<{
														item: string;
														strength: number;
													}>
												).map(({ item, strength }) => (
													<ProductDetailsItemContent
														key={`complaints-${item}`}
														characteristic={
															// @ts-ignore: dynamic key generation
															t(`product:filters.complaints.${item}`)
														}
														strength={strength}
														strengthMax={terpenes.length}
														strengthLabel={getStrengthLabel(
															strength,
															terpenes.length,
															"complaints",
														)}
														variant="with-strength"
													/>
												))}
											</ul>
										</ProductDetailsItem>
									</Accordion.Item>

									<Accordion.Item value="effects">
										<ProductDetailsItem
											title={t("common:effects")}
											info={t("common:effectsInfo")}
											secondaryCharacteristic={details.effects.rest.map(
												(e: string) =>
													// @ts-ignore: dynamic key generation
													t(`product:filters.effects.${e}`),
											)}
											hasData={enabledSections.effects}
										>
											<ul className="mt-4 mb-2">
												{(
													Object.values(details.effects.best) as Array<{
														item: string;
														strength: number;
													}>
												).map(({ item, strength }) => {
													const label = t(
														// biome-ignore lint/suspicious/noExplicitAny: dynamic key generation
														`product:filters.effects.${item}` as any,
													);
													return (
														<ProductDetailsItemContent
															key={`effects-${item}`}
															characteristic={label}
															icon={
																<EffectsIcon
																	name={item}
																	size="md"
																	className="text-gray-400"
																/>
															}
															strength={strength}
															strengthMax={terpenes.length}
															strengthLabel={getStrengthLabel(
																strength,
																terpenes.length,
																"effects",
															)}
															variant="with-strength"
														/>
													);
												})}
											</ul>
										</ProductDetailsItem>
									</Accordion.Item>

									<Accordion.Item value="flavors">
										<ProductDetailsItem
											title={t("common:flavors")}
											info={t("common:flavorsInfo")}
											secondaryCharacteristic={details.flavors.rest.map(
												(f: string) =>
													// @ts-ignore: dynamic key generation
													t(`product:filters.flavors.${f}`),
											)}
											hasData={enabledSections.flavors}
										>
											<ul className="mt-4 mb-2">
												{(
													Object.values(details.flavors.best) as Array<{
														item: string;
														strength: number;
													}>
												).map(({ item, strength }) => {
													const label = t(
														// biome-ignore lint/suspicious/noExplicitAny: dynamic key generation
														`product:filters.flavors.${item}` as any,
													);
													return (
														<ProductDetailsItemContent
															key={`flavors-${item}`}
															characteristic={label}
															strength={strength}
															strengthMax={terpenes.length}
															strengthLabel={getStrengthLabel(
																strength,
																terpenes.length,
																"flavors",
															)}
															variant="with-strength"
														/>
													);
												})}
											</ul>
										</ProductDetailsItem>
									</Accordion.Item>
								</>
							)}
							{shouldShowEdibleTaste(product.category) && (
								<Accordion.Item value="taste">
									<ProductDetailsItem
										title={t("common:taste")}
										secondaryCharacteristic={[]}
										hasData={enabledSections.taste}
									>
										<ul className="mt-4 mb-2">
											<ProductDetailsItemContent
												key={`taste-${thc}`}
												characteristic={
													edibleTaste ||
													t(getTasteTranslationKey(thc), {
														ns: "product",
													})
												}
												variant="default"
												strength={0}
												strengthMax={0}
											/>
										</ul>
									</ProductDetailsItem>
								</Accordion.Item>
							)}

							{showTerpeneBasedSections && (
								<Accordion.Item value="terpenes">
									<ProductDetailsTerpenes
										terpenes={terpenes}
										variant={terpenes.length > 0 ? "default" : "noData"}
									/>
								</Accordion.Item>
							)}

							<Accordion.Item value="producer">
								<ProductDetailsProducer producer={producer} />
							</Accordion.Item>
						</Accordion.Root>
					</Suspense>

					{showExclusiveProductsSection && (
						<div className="[grid-area:exclusive] md:hidden">
							<ExclusiveProductsPdpSection products={exclusiveProducts} />
						</div>
					)}
				</div>
			</div>
		</div>
	);
}
